import { useEffect } from "react";

const APP_TITLE = "Above Security";

/**
 * Build a page-scoped document title in the form "X | Above Security".
 * With a page: "Subprocessors | Above Security". Without one (the index): "Trust Center | Above Security".
 */
export function buildPageTitle(page?: string): string {
	return page ? `${page} | ${APP_TITLE}` : `Trust Center | ${APP_TITLE}`;
}

interface PageMeta {
	/** Page title segment (prefixed onto the app title). */
	title: string;
	/** Meta description for crawlers/social. */
	description: string;
}

/**
 * Set the document `<title>` and `<meta name="description">` for the current page.
 *
 * The Trust Center is intentionally indexable (a trust center exists to be found
 * by prospects and security reviewers), so each route updates the title + meta
 * description on mount. SPA routing doesn't re-render index.html, so we mutate the
 * tags imperatively; the description tag is created if absent.
 * Source: https://developer.mozilla.org/en-US/docs/Web/API/Document/title
 */
export function usePageMeta({ title, description }: PageMeta): void {
	useEffect(() => {
		document.title = buildPageTitle(title);

		let descriptionTag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
		if (!descriptionTag) {
			descriptionTag = document.createElement("meta");
			descriptionTag.name = "description";
			document.head.appendChild(descriptionTag);
		}
		descriptionTag.content = description;
	}, [title, description]);
}
